import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, Settings, Users, ChevronLeft, ChevronRight, 
  LogOut, ChevronUp, ChevronDown, User, Shield, TestTube2, Building2,
  BarChart3, CreditCard, UserCog, Home, X, Brain, Mail, Plug, Sparkles,
  HelpCircle, Play, PresentationIcon, Flag, MessageSquareText, Coins, KeyRound
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useFeatureFlags, FEATURE_IDS } from '../../hooks/useFeatureFlags';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import NotificationBell from '../notifications/NotificationBell';
import OrganizationSwitcher from './OrganizationSwitcher';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { organization, user, logout, impersonation, isImpersonating, stopImpersonation, originalAdmin } = useAuth();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { getThemeColors } = useSafeMode();
  const { isCollaborator, role } = usePermissions();
  const { isEnabled } = useFeatureFlags();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ admin: true });
  
  const colors = getThemeColors();
  
  // Check if user is billing_admin - they don't need a sidebar
  const isBillingAdmin = user?.teamRole === 'billing_admin' || role === 'billing_admin';
  const isFreeProduct = organization?.account_type === 'free_product';
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleExitImpersonation = () => {
    stopImpersonation();
    navigate('/admin/organizations');
  };
  
  // Billing admins don't see the sidebar at all
  if (isBillingAdmin && !isImpersonating) {
    return null;
  }
  
  // Free product users don't see a sidebar — avatar is embedded in page headers
  if (isFreeProduct && !isImpersonating) {
    return null;
  }
  
  // When impersonating, use the impersonated role, otherwise use real role
  const isAdmin = isImpersonating ? false : user?.role === 'admin';
  const realAdmin = originalAdmin?.user?.role === 'admin';
  
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Build main nav items with feature flag filtering
  // Collaborators only see Products (not Dashboard, Executive AI, Visualizations)
  const mainNavItems = [
    !isCollaborator && isEnabled(FEATURE_IDS.ORG_EXECUTIVE_AI) && { path: '/executive-ai', label: 'Executive AI', icon: Sparkles },
    !isCollaborator && { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/products', label: 'Products', icon: Package },
    !isCollaborator && isEnabled(FEATURE_IDS.ORG_VISUALIZATIONS) && { path: '/visualizations', label: 'Visualizations', icon: PresentationIcon },
  ].filter(Boolean);

  // Navigation structure with groups
  // When impersonating, show the organization's nav structure (not admin)
  const navGroups = [
    // Main section - for regular users or when impersonating
    ...(!isAdmin ? [{
      id: 'main',
      label: 'Main',
      items: mainNavItems
    }] : []),
    
    // Resources section - for regular users (Help & Documentation)
    ...(!isAdmin ? [{
      id: 'resources',
      label: 'Resources',
      items: [
        { path: '/help', label: 'Help & Docs', icon: HelpCircle, external: true },
      ]
    }] : []),
    
    // Admin section - only for admins when NOT impersonating
    ...(isAdmin && !isImpersonating ? [{
      id: 'admin',
      label: 'Admin',
      collapsible: true,
      items: [
        { path: '/admin', label: 'Dashboard', icon: BarChart3 },
        { path: '/admin/market-intel', label: 'Market Intelligence', icon: MessageSquareText },
        { path: '/admin/customers', label: 'Customers', icon: Users },
        { path: '/admin/demo', label: 'Demo Mgmt', icon: Play },
        { path: '/admin/roles', label: 'Roles', icon: Shield },
        { path: '/admin/testing', label: 'Test Accounts', icon: TestTube2 },
        { path: '/admin/plans', label: 'Plan Management', icon: CreditCard },
        { path: '/admin/token-pricing', label: 'Token Pricing', icon: Coins },
        { path: '/admin/mcp', label: 'MCP', icon: Plug },
      ]
    }] : []),
    
    // Settings section - admin only (non-admins use the user menu for Account Settings)
    ...(isAdmin && !isImpersonating ? [{
      id: 'settings',
      label: 'Settings',
      items: [
        { path: '/admin/free-tier', label: 'Free Tier', icon: Sparkles },
        { path: '/admin/features', label: 'Features', icon: Flag },
        { path: '/admin/settings', label: 'System', icon: Settings },
        { path: '/admin/email', label: 'Email', icon: Mail },
        { path: '/admin/ai-prompts', label: 'AI Prompts', icon: Brain },
        { path: '/admin/api-keys', label: 'API Keys', icon: KeyRound },
      ]
    }] : []),
  ];

  const isPathActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div 
      className={cn(
        "flex flex-col h-screen sticky top-0 transition-all duration-300 ease-in-out relative border-r",
        isCollapsed ? "w-16" : "w-64",
        colors.bgSecondary,
        colors.borderPrimary
      )}
    >
      {/* Impersonation Banner - Always visible at top when impersonating */}
      {isImpersonating && (
        <div className={cn(
          "bg-amber-500 text-white",
          isCollapsed ? "p-2" : "px-4 py-3"
        )}>
          {isCollapsed ? (
            <button 
              onClick={handleExitImpersonation}
              className="w-full flex justify-center"
              title="Exit impersonation"
            >
              <X className="w-5 h-5" />
            </button>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium opacity-90">Viewing as:</p>
                <p className="text-sm font-semibold truncate">{impersonation?.user_name || 'Unknown User'}</p>
                <p className="text-xs opacity-75 truncate">{impersonation?.organization_name}</p>
              </div>
              <button 
                onClick={handleExitImpersonation}
                className="ml-2 p-1.5 hover:bg-amber-600 rounded transition-colors flex-shrink-0"
                title="Exit impersonation"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className={cn(
        "border-b",
        isCollapsed ? "p-3" : "p-4",
        colors.borderPrimary
      )}>
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0 transition-all duration-300",
            colors.logoGradient
          )}>
            <span className="text-white font-bold text-base">SR</span>
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <h1 className={cn("font-semibold text-base truncate", colors.textPrimary)}>
                StackRanked
              </h1>
              {/* Show impersonated org or regular org */}
              {isImpersonating ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={cn("text-xs truncate", colors.textMuted)}>
                    {impersonation?.organization_name}
                  </span>
                  {impersonation?.plan_tier === 'paid' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-600">
                      Pro
                    </span>
                  )}
                </div>
              ) : !isAdmin && organization?.name ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={cn("text-xs truncate", colors.textMuted)}>
                    {organization.name}
                  </span>
                  {organization?.plan_tier === 'paid' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-600">
                      Pro
                    </span>
                  )}
                </div>
              ) : null}
              {isAdmin && !isImpersonating && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-600 mt-0.5">
                  Admin Mode
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Organization Switcher - only for non-admin, non-impersonating users */}
      {!isCollapsed && !isAdmin && !isImpersonating && (
        <div className="px-2 mb-2">
          <OrganizationSwitcher />
        </div>
      )}
      
      {/* Collapse Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleSidebar}
        className={cn(
          "absolute -right-3 w-6 h-6 rounded-full shadow-md z-50 transition-all border",
          "hover:scale-110",
          isImpersonating ? "top-28" : "top-16",
          colors.bgSecondary,
          colors.borderPrimary,
          colors.bgHover
        )}
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? (
          <ChevronRight className={cn("w-3 h-3", colors.textMuted)} />
        ) : (
          <ChevronLeft className={cn("w-3 h-3", colors.textMuted)} />
        )}
      </Button>

      {/* Navigation */}
      <nav className={cn(
        "flex-1 overflow-y-auto",
        isCollapsed ? "p-2" : "p-3"
      )}>
        {navGroups.map((group, groupIndex) => (
          <div key={group.id} className={cn(groupIndex > 0 && "mt-4")}>
            {/* Group Header */}
            {!isCollapsed && group.label && (
              <div 
                className={cn(
                  "flex items-center justify-between px-3 py-1.5 mb-1",
                  group.collapsible && "cursor-pointer hover:bg-gray-100/50 rounded-md transition-colors"
                )}
                onClick={() => group.collapsible && toggleGroup(group.id)}
              >
                <span className={cn("text-[11px] font-semibold uppercase tracking-wider", colors.textMuted)}>
                  {group.label}
                </span>
                {group.collapsible && (
                  <ChevronDown className={cn(
                    "w-3 h-3 transition-transform",
                    colors.textMuted,
                    !expandedGroups[group.id] && "-rotate-90"
                  )} />
                )}
              </div>
            )}
            
            {/* Group Items */}
            {(!group.collapsible || expandedGroups[group.id] || isCollapsed) && (
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    target={item.external ? '_blank' : undefined}
                    rel={item.external ? 'noopener noreferrer' : undefined}
                    title={isCollapsed ? item.label : undefined}
                    className={cn(
                      'flex items-center rounded-lg text-sm font-medium transition-all duration-150',
                      isCollapsed ? 'justify-center p-2.5' : 'gap-3 px-3 py-2',
                      isPathActive(item.path)
                        ? cn(colors.activeNav, "shadow-sm")
                        : cn(colors.textSecondary, "hover:bg-gray-100 dark:hover:bg-gray-800")
                    )}
                  >
                    <item.icon className={cn(
                      "flex-shrink-0 transition-colors",
                      isCollapsed ? "w-5 h-5" : "w-4 h-4",
                      isPathActive(item.path) && "text-current"
                    )} />
                    {!isCollapsed && <span>{item.label}</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Notifications */}
      <div className={cn(
        "border-t",
        isCollapsed ? "p-2" : "px-3 py-2",
        colors.borderPrimary
      )}>
        <NotificationBell showLabel={!isCollapsed} />
      </div>

      {/* User Profile with Dropdown */}
      <div className={cn(
        "border-t",
        isCollapsed ? "p-2" : "p-3",
        colors.borderPrimary
      )}>
        <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              title={isCollapsed ? (isImpersonating ? impersonation?.user_name : user?.name) : undefined}
              className={cn(
                "flex items-center rounded-lg transition-colors cursor-pointer w-full",
                isCollapsed ? "justify-center p-2" : "gap-3 px-3 py-2",
                "hover:bg-gray-100 dark:hover:bg-gray-800",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                userMenuOpen && colors.bgActive
              )}
            >
              {/* Show impersonated user avatar or real user avatar */}
              {isImpersonating ? (
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 text-sm bg-amber-500"
                )}>
                  {impersonation?.user_name?.charAt(0) || 'U'}
                </div>
              ) : user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user?.name}
                  className={cn("w-8 h-8 rounded-full object-cover ring-2 flex-shrink-0", colors.ringColor)}
                />
              ) : (
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 text-sm",
                  colors.logoGradient
                )}>
                  {user?.name?.charAt(0) || 'U'}
                </div>
              )}
              {!isCollapsed && (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <p className={cn("text-sm font-medium truncate", colors.textPrimary)}>
                      {isImpersonating ? impersonation?.user_name : user?.name}
                    </p>
                    <p className={cn("text-xs truncate", colors.textMuted)}>
                      {isImpersonating 
                        ? `${impersonation?.user_role || 'member'} (impersonating)`
                        : (isAdmin ? 'Administrator' : user?.email)
                      }
                    </p>
                  </div>
                  <ChevronUp className={cn(
                    "w-4 h-4 transition-transform flex-shrink-0",
                    colors.textMuted,
                    userMenuOpen ? "rotate-180" : ""
                  )} />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align={isCollapsed ? "center" : "end"} 
            side="top" 
            sideOffset={8}
            className={cn("w-56", colors.bgCard, colors.borderPrimary)}
          >
            <div className={cn("px-2 py-1.5", colors.textMuted)}>
              <p className={cn("text-sm font-medium", colors.textPrimary)}>
                {isImpersonating ? impersonation?.user_name : user?.name}
              </p>
              <p className="text-xs truncate">
                {isImpersonating ? impersonation?.user_email : user?.email}
              </p>
              {isImpersonating && (
                <p className="text-xs text-amber-600 mt-1">Viewing as this user</p>
              )}
            </div>
            <DropdownMenuSeparator className={colors.borderPrimary} />
            {isImpersonating && (
              <>
                <DropdownMenuItem 
                  onClick={handleExitImpersonation}
                  className={cn("cursor-pointer text-amber-600 focus:text-amber-600 focus:bg-amber-500/10")}
                >
                  <X className="w-4 h-4 mr-2" />
                  Exit Impersonation
                </DropdownMenuItem>
                <DropdownMenuSeparator className={colors.borderPrimary} />
              </>
            )}
            <DropdownMenuItem 
              onClick={() => navigate('/settings')}
              className={cn("cursor-pointer", colors.bgHover)}
            >
              <User className="w-4 h-4 mr-2" />
              Account Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className={colors.borderPrimary} />
            <DropdownMenuItem 
              onClick={handleLogout}
              className={cn("cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-500/10")}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default Sidebar;
